import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import conversationHistoryService from '../services/ConversationHistoryService';
import ticketService from '../services/TicketService';

const STATUS_OPTIONS = [
    { value: 'open', label: 'open' },
    { value: 'pending', label: 'pending' },
    { value: 'resolved', label: 'resolved' },
    { value: 'closed', label: 'closed' },
];

const CITATION_RE = /\[([^[\]]+?):([a-f0-9]{20,})\/(\d+)\]/g;

function normalizeStatus(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === 'open') return 'open';
    if (raw === 'pending') return 'pending';
    if (raw === 'resolved') return 'resolved';
    if (raw === 'close' || raw === 'closed') return 'closed';
    return raw || 'open';
}

function statusLabel(t, value) {
    switch (normalizeStatus(value)) {
        case 'open':
            return t('tickets.statusOpen', 'Open');
        case 'pending':
            return t('tickets.statusPending', 'Pending');
        case 'resolved':
            return t('tickets.statusResolved', 'Resolved');
        case 'closed':
            return t('tickets.statusClosed', 'Closed');
        default:
            return normalizeStatus(value);
    }
}

function formatEpoch(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '-';
    const ms = n < 1e12 ? n * 1000 : n;
    try {
        return new Date(ms).toLocaleString();
    } catch (e) {
        return String(value);
    }
}

function formatBytes(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function getAttachmentPreviewType(attachment) {
    const contentType = String(attachment?.contentType || '').toLowerCase();
    const filename = String(attachment?.filename || '').toLowerCase();
    if (contentType.startsWith('image/') || filename.match(/\.(png|jpe?g|gif|webp)$/)) return 'image';
    if (contentType.startsWith('video/') || filename.match(/\.(mp4|mov|m4v|webm|ogv)$/)) return 'video';
    if (contentType === 'application/pdf' || filename.endsWith('.pdf')) return 'pdf';
    return '';
}

function getMessageText(message) {
    if (typeof message?.messageContent === 'string') return message.messageContent;
    if (message?.messageContent != null) return String(message.messageContent);
    if (typeof message?.content === 'string') return message.content;
    if (typeof message?.message === 'string') return message.message;
    return '';
}

function parseCitations(text) {
    if (!text) return text;
    const parts = [];
    let lastIndex = 0;
    let match;
    const regex = new RegExp(CITATION_RE.source, 'g');
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
        parts.push({ type: 'citation', fileName: match[1], documentId: match[2], page: match[3] });
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 0 ? parts : text;
}

function CitationMarker({ fileName, page }) {
    const label = `${fileName}, p.${page}`;
    return (
        <Tooltip title={label} arrow>
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    bgcolor: '#d32f2f',
                    color: '#fff',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    mx: 0.35,
                    verticalAlign: 'middle',
                }}
            >
                {page}
            </Box>
        </Tooltip>
    );
}

function renderMessageTextWithCitations(text) {
    const parts = parseCitations(text);
    if (typeof parts === 'string') return parts;
    return parts.map((part, index) => (
        typeof part === 'string'
            ? part
            : <CitationMarker key={`${part.documentId}-${part.page}-${index}`} fileName={part.fileName} page={part.page} />
    ));
}

function renderMarkdownChildrenWithCitations(children) {
    if (typeof children === 'string') return renderMessageTextWithCitations(children);
    if (!Array.isArray(children)) return children;
    return children.map((child, index) => (
        typeof child === 'string'
            ? <React.Fragment key={index}>{renderMessageTextWithCitations(child)}</React.Fragment>
            : child
    ));
}

function SupportTicketDetails() {
    const { t } = useTranslation();
    const { supportTicketId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [ticket, setTicket] = useState(null);
    const [noteBody, setNoteBody] = useState('');
    const [noteVisibleToCustomer, setNoteVisibleToCustomer] = useState(false);
    const [addingNote, setAddingNote] = useState(false);
    const [savingStatus, setSavingStatus] = useState(false);
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const [attachmentUpload, setAttachmentUpload] = useState({ filename: '', progress: 0 });
    const [isDraggingAttachment, setIsDraggingAttachment] = useState(false);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [conversationMessages, setConversationMessages] = useState([]);
    const [loadingConversation, setLoadingConversation] = useState(false);
    const [conversationError, setConversationError] = useState('');
    const attachmentDragDepthRef = useRef(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        const load = async () => {
            if (!supportTicketId) return;
            setLoading(true);
            try {
                const data = await ticketService.getSupportTicket(supportTicketId);
                setTicket(data || null);
            } catch (e) {
                console.error('Failed to load support ticket', e);
                setSnackbar({ open: true, message: t('tickets.failedToLoadSupportTicket', 'Failed to load support ticket'), severity: 'error' });
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [supportTicketId, t]);

    useEffect(() => {
        const conversationId = String(ticket?.conversationId || '').trim();
        if (!conversationId) {
            setConversationMessages([]);
            setConversationError('');
            return undefined;
        }

        let cancelled = false;
        const loadConversation = async () => {
            setLoadingConversation(true);
            setConversationError('');
            try {
                const details = await conversationHistoryService.getConversationDetail(conversationId);
                const messages = Array.isArray(details?.messages) ? details.messages : [];
                if (!cancelled) {
                    setConversationMessages(messages.filter((message) => String(message?.messageType || '').toLowerCase() !== 'system'));
                }
            } catch (e) {
                console.error('Failed to load support ticket conversation history', e);
                if (!cancelled) {
                    setConversationMessages([]);
                    setConversationError(t('tickets.failedToLoadConversationHistory', 'Failed to load conversation history'));
                }
            } finally {
                if (!cancelled) setLoadingConversation(false);
            }
        };

        loadConversation();
        return () => { cancelled = true; };
    }, [ticket?.conversationId, t]);

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));
    const notes = Array.isArray(ticket?.notes) ? ticket.notes : [];
    const attachments = Array.isArray(ticket?.attachments) ? ticket.attachments : [];
    const status = normalizeStatus(ticket?.status);
    const previewType = getAttachmentPreviewType(previewAttachment);
    const previewUrl = previewAttachment?.previewUrl || previewAttachment?.downloadUrl || '';

    const handleAddNote = async () => {
        const trimmed = noteBody.trim();
        if (!supportTicketId || !trimmed) {
            setSnackbar({ open: true, message: t('tickets.noteRequired', 'Note is required'), severity: 'error' });
            return;
        }

        setAddingNote(true);
        try {
            const updated = await ticketService.addSupportTicketNote(
                supportTicketId,
                trimmed,
                noteVisibleToCustomer,
            );
            setTicket(updated || null);
            setNoteBody('');
            setNoteVisibleToCustomer(false);
            setSnackbar({ open: true, message: t('tickets.noteAdded', 'Note added'), severity: 'success' });
        } catch (e) {
            console.error('Failed to add support ticket note', e);
            setSnackbar({ open: true, message: t('tickets.failedToAddNote', 'Failed to add note'), severity: 'error' });
        } finally {
            setAddingNote(false);
        }
    };

    const handleStatusChange = async (event) => {
        const nextStatus = normalizeStatus(event.target.value);
        if (!supportTicketId || nextStatus === status) return;
        setSavingStatus(true);
        try {
            const updated = await ticketService.updateSupportTicket(supportTicketId, { status: nextStatus });
            setTicket(updated || null);
            setSnackbar({ open: true, message: t('tickets.statusUpdated', 'Status updated'), severity: 'success' });
        } catch (e) {
            console.error('Failed to update support ticket status', e);
            setSnackbar({ open: true, message: t('tickets.failedToUpdateStatus', 'Failed to update status'), severity: 'error' });
        } finally {
            setSavingStatus(false);
        }
    };

    const uploadAttachmentFile = async (file) => {
        if (!supportTicketId || !file) return;

        setUploadingAttachment(true);
        setAttachmentUpload({ filename: file.name || 'attachment', progress: 0 });
        try {
            const updated = await ticketService.uploadSupportTicketAttachment(supportTicketId, file, (progress) => {
                setAttachmentUpload({ filename: file.name || 'attachment', progress });
            });
            setAttachmentUpload({ filename: file.name || 'attachment', progress: 100 });
            setTicket(updated || null);
            setSnackbar({ open: true, message: t('tickets.attachmentUploaded', 'Attachment uploaded'), severity: 'success' });
        } catch (e) {
            console.error('Failed to upload support ticket attachment', e);
            setSnackbar({ open: true, message: t('tickets.failedToUploadAttachment', 'Failed to upload attachment'), severity: 'error' });
        } finally {
            setUploadingAttachment(false);
            setAttachmentUpload({ filename: '', progress: 0 });
            setIsDraggingAttachment(false);
            attachmentDragDepthRef.current = 0;
        }
    };

    const handleAttachmentChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) uploadAttachmentFile(file);
    };

    const handleAttachmentDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        attachmentDragDepthRef.current += 1;
        if (!uploadingAttachment) setIsDraggingAttachment(true);
    };

    const handleAttachmentDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    };

    const handleAttachmentDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        attachmentDragDepthRef.current = Math.max(0, attachmentDragDepthRef.current - 1);
        if (attachmentDragDepthRef.current === 0) setIsDraggingAttachment(false);
    };

    const handleAttachmentDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        attachmentDragDepthRef.current = 0;
        setIsDraggingAttachment(false);
        const file = event.dataTransfer?.files?.[0];
        if (file && !uploadingAttachment) uploadAttachmentFile(file);
    };

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5" fontWeight="bold">{t('tickets.supportTicket', 'Support Ticket')}</Typography>
                <Button variant="outlined" onClick={() => navigate('/tickets?tab=support')} disabled={loading}>
                    {t('tickets.backToTickets', 'Back to Tickets')}
                </Button>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(320px, 380px)' },
                    gap: 2,
                    alignItems: 'start',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <Paper sx={{ p: 2 }} elevation={1}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(auto-fit, minmax(150px, 1fr))',
                        },
                        gap: 1,
                        mb: 2,
                    }}
                >
                    {[
                        [t('tickets.supportTicketId', 'Support Ticket ID'), ticket?.supportTicketId || supportTicketId || '-'],
                        [t('tickets.createdBy', 'Created By'), ticket?.createdBy || '-'],
                        [t('tickets.requesterEmail', 'Requester Email'), ticket?.requesterEmail || '-'],
                        [t('tickets.created', 'Created'), formatEpoch(ticket?.createdAt)],
                        [t('tickets.updated', 'Updated'), formatEpoch(ticket?.updatedAt)],
                    ].map(([label, value]) => (
                        <Box key={label} sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                {label}
                            </Typography>
                            <Typography variant="body2" color="text.primary" noWrap title={String(value)}>
                                {value}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {loading ? <Alert severity="info" sx={{ mb: 2 }}>{t('tickets.loading', 'Loading…')}</Alert> : null}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            md: 'minmax(0, 2fr) repeat(3, minmax(0, 1fr))',
                        },
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <TextField
                        label={t('tickets.title', 'Title')}
                        fullWidth
                        size="small"
                        value={ticket?.title || ''}
                        disabled
                    />
                    <TextField
                        label={t('tickets.status', 'Status')}
                        select
                        fullWidth
                        size="small"
                        value={status}
                        onChange={handleStatusChange}
                        disabled={loading || savingStatus || !ticket}
                    >
                        {STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {statusLabel(t, opt.value)}
                            </MenuItem>
                        ))}
                        {!STATUS_OPTIONS.some((opt) => opt.value === status) ? (
                            <MenuItem value={status}>{statusLabel(t, status)}</MenuItem>
                        ) : null}
                    </TextField>
                    <TextField
                        label={t('tickets.source', 'Source')}
                        fullWidth
                        size="small"
                        value={ticket?.source || '-'}
                        disabled
                    />
                    <TextField
                        label={t('tickets.conversationId', 'Conversation ID')}
                        fullWidth
                        size="small"
                        value={ticket?.conversationId || '-'}
                        disabled
                    />
                </Box>

                <TextField
                    label={t('tickets.description', 'Description')}
                    fullWidth
                    size="small"
                    multiline
                    minRows={4}
                    maxRows={10}
                    value={ticket?.description || ''}
                    disabled
                />
            </Paper>

            <Paper sx={{ p: 2 }} elevation={1}>
                <Typography variant="h6" sx={{ mb: 1 }}>{t('tickets.notes', 'Notes')}</Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                        gap: 1.5,
                        alignItems: 'end',
                        mb: notes.length ? 2 : 1,
                    }}
                >
                    <Box>
                        <TextField
                            label={t('tickets.addNote', 'Add Note')}
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            maxRows={5}
                            value={noteBody}
                            onChange={(e) => setNoteBody(e.target.value)}
                            disabled={loading || addingNote}
                        />
                        <FormControlLabel
                            sx={{ mt: 0.5 }}
                            control={(
                                <Checkbox
                                    checked={noteVisibleToCustomer}
                                    onChange={(e) => setNoteVisibleToCustomer(e.target.checked)}
                                    disabled={loading || addingNote}
                                    size="small"
                                />
                            )}
                            label={t('tickets.visibleToCustomer', 'Visible to customer')}
                        />
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={handleAddNote}
                        disabled={!supportTicketId || addingNote || !noteBody.trim()}
                        sx={{ minWidth: 116, height: 40 }}
                    >
                        {addingNote ? t('tickets.adding', 'Adding…') : t('tickets.addNote', 'Add Note')}
                    </Button>
                </Box>

                {notes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('tickets.noNotes', 'No notes.')}</Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {[...notes].sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0)).map((note, index) => (
                            <Box
                                key={note?.noteId || `${note?.createdAt || ''}-${index}`}
                                sx={{ borderLeft: '3px solid', borderColor: note?.visibleToCustomer ? 'success.main' : 'divider', pl: 1.5 }}
                            >
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {note?.body || ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {[
                                        note?.createdBy || '-',
                                        formatEpoch(note?.createdAt),
                                        note?.visibleToCustomer ? t('tickets.visibleToCustomer', 'Visible to customer') : null,
                                    ].filter(Boolean).join(' - ')}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Paper>

            <Paper
                sx={{
                    p: 2,
                    position: 'relative',
                    border: '1px solid',
                    borderColor: isDraggingAttachment ? 'primary.main' : 'transparent',
                    bgcolor: isDraggingAttachment ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
                    transition: 'border-color 120ms ease, background-color 120ms ease',
                }}
                elevation={1}
                onDragEnter={handleAttachmentDragEnter}
                onDragOver={handleAttachmentDragOver}
                onDragLeave={handleAttachmentDragLeave}
                onDrop={handleAttachmentDrop}
            >
                {isDraggingAttachment ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 8,
                            border: '2px dashed',
                            borderColor: 'primary.main',
                            borderRadius: 1,
                            bgcolor: 'rgba(255, 255, 255, 0.72)',
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        <Typography variant="subtitle1" color="primary" fontWeight={600}>
                            {t('tickets.dropAttachmentToUpload', 'Drop attachment to upload')}
                        </Typography>
                    </Box>
                ) : null}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                    <Typography variant="h6">{t('tickets.attachments', 'Attachments')}</Typography>
                    <Button
                        component="label"
                        variant="outlined"
                        disabled={!supportTicketId || loading || uploadingAttachment}
                    >
                        {uploadingAttachment ? t('tickets.uploading', 'Uploading…') : t('tickets.addAttachment', 'Add Attachment')}
                        <input type="file" hidden onChange={handleAttachmentChange} />
                    </Button>
                </Box>
                {uploadingAttachment ? (
                    <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" noWrap title={attachmentUpload.filename}>
                                {attachmentUpload.filename || t('tickets.uploadingAttachment', 'Uploading attachment')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {attachmentUpload.progress}%
                            </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={attachmentUpload.progress} />
                    </Box>
                ) : null}

                {attachments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('tickets.noAttachments', 'No attachments.')}</Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {attachments.map((attachment, index) => {
                            const filename = attachment?.filename || 'attachment';
                            const meta = [
                                formatBytes(attachment?.size),
                                formatEpoch(attachment?.uploadedAt),
                                attachment?.uploadedBy ? t('tickets.attachedBy', 'Attached by {{name}}', { name: attachment.uploadedBy }) : '',
                            ]
                                .filter(Boolean)
                                .join(' - ');
                            const attachmentPreviewType = getAttachmentPreviewType(attachment);
                            const canPreview = Boolean(attachmentPreviewType && (attachment?.previewUrl || attachment?.downloadUrl));
                            return (
                                <Box key={attachment?.fileId || `${filename}-${index}`}>
                                    {canPreview ? (
                                        <Link
                                            component="button"
                                            type="button"
                                            underline="hover"
                                            onClick={() => setPreviewAttachment(attachment)}
                                            sx={{ border: 0, p: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                                        >
                                            {filename}
                                        </Link>
                                    ) : attachment?.downloadUrl ? (
                                        <Link href={attachment.downloadUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                                            {filename}
                                        </Link>
                                    ) : (
                                        <Typography variant="body2">{filename}</Typography>
                                    )}
                                    {meta ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {meta}
                                        </Typography>
                                    ) : null}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Paper>
                </Box>

                <Paper
                    sx={{
                        p: 2,
                        position: { lg: 'sticky' },
                        top: { lg: 16 },
                        maxHeight: { lg: 'calc(100vh - 32px)' },
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 280,
                    }}
                    elevation={1}
                >
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                        {t('tickets.conversationHistory', 'Conversation History')}
                    </Typography>

                    {!ticket?.conversationId ? (
                        <Typography variant="body2" color="text.secondary">
                            {t('tickets.noConversationLinked', 'No conversation linked')}
                        </Typography>
                    ) : loadingConversation ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                                {t('tickets.loadingConversationHistory', 'Loading conversation history...')}
                            </Typography>
                        </Box>
                    ) : conversationError ? (
                        <Alert severity="error">{conversationError}</Alert>
                    ) : conversationMessages.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {t('tickets.noConversationMessages', 'No conversation messages found.')}
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, overflowY: 'auto', pr: 0.5 }}>
                            {conversationMessages.map((message, index) => {
                                const role = String(message?.messageType || '').toLowerCase();
                                const isUser = role === 'user';
                                const isBot = role === 'bot' || role === 'assistant';
                                const text = getMessageText(message);
                                return (
                                    <Box
                                        key={message?.conversationHistoryId || `${message?.createdAt || ''}-${index}`}
                                        sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}
                                    >
                                        <Box
                                            sx={{
                                                maxWidth: '88%',
                                                bgcolor: isUser ? 'primary.main' : 'grey.100',
                                                color: isUser ? 'common.white' : 'text.primary',
                                                border: '1px solid',
                                                borderColor: isUser ? 'primary.main' : 'divider',
                                                borderRadius: 1.5,
                                                px: 1.25,
                                                py: 0.9,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.4, opacity: 0.75, textTransform: 'capitalize' }}>
                                                {role || t('tickets.message', 'message')}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                component="div"
                                                sx={{
                                                    overflowWrap: 'anywhere',
                                                    wordBreak: 'break-word',
                                                    '& a': {
                                                        color: isUser ? 'inherit' : 'primary.main',
                                                        fontWeight: 500,
                                                        textDecoration: 'underline',
                                                        textUnderlineOffset: '2px',
                                                        wordBreak: 'break-all',
                                                    },
                                                    '& h1': { fontSize: '1rem', fontWeight: 700, m: 0, mb: 0.75 },
                                                    '& h2': { fontSize: '0.95rem', fontWeight: 700, m: 0, mb: 0.75 },
                                                    '& h3': { fontSize: '0.9rem', fontWeight: 600, m: 0, mb: 0.75 },
                                                    '& p': { m: 0, mb: 0.75 },
                                                    '& p:last-child': { mb: 0 },
                                                    '& ul, & ol': { m: 0, pl: 2.25, mb: 0.75 },
                                                    '& li': { mb: 0.5 },
                                                    '& li:last-child': { mb: 0 },
                                                    '& li > p': { display: 'inline', m: 0 },
                                                    '& pre': { whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto', m: 0 },
                                                    '& code': { wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
                                                    '& table': { display: 'block', width: '100%', overflowX: 'auto', borderCollapse: 'collapse', my: 0.75, fontSize: '0.8rem' },
                                                    '& th, & td': { border: '1px solid', borderColor: 'rgba(0,0,0,0.18)', px: 0.75, py: 0.4, textAlign: 'left' },
                                                    '& th': { fontWeight: 700, bgcolor: 'rgba(0,0,0,0.04)' },
                                                }}
                                            >
                                                {text ? (
                                                    isBot ? (
                                                        <ReactMarkdown
                                                            remarkPlugins={[remarkGfm]}
                                                            components={{
                                                                a: ({ node, children, ...props }) => (
                                                                    <a {...props} target="_blank" rel="noreferrer">
                                                                        {children}
                                                                    </a>
                                                                ),
                                                                p: ({ node, children }) => (
                                                                    <p>{renderMarkdownChildrenWithCitations(children)}</p>
                                                                ),
                                                                li: ({ node, children }) => (
                                                                    <li>{renderMarkdownChildrenWithCitations(children)}</li>
                                                                ),
                                                            }}
                                                        >
                                                            {text}
                                                        </ReactMarkdown>
                                                    ) : (
                                                        renderMessageTextWithCitations(text)
                                                    )
                                                ) : '-'}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.6, opacity: 0.72, textAlign: 'right' }}>
                                                {formatEpoch(message?.createdAt)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Paper>
            </Box>

            <Dialog
                open={Boolean(previewAttachment)}
                onClose={() => setPreviewAttachment(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { overflow: 'hidden' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ flex: 1, fontWeight: 600 }} noWrap>
                        {previewAttachment?.filename || t('tickets.attachmentPreview', 'Attachment Preview')}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: '#f5f5f5', minHeight: 360 }}>
                    {previewAttachment && previewType === 'image' ? (
                        <Box sx={{ minHeight: 360, height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                            <img
                                src={previewUrl}
                                alt={previewAttachment?.filename || t('tickets.attachmentPreviewAlt', 'Attachment preview')}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                        </Box>
                    ) : null}
                    {previewAttachment && previewType === 'pdf' ? (
                        <iframe
                            src={previewUrl}
                            title={previewAttachment?.filename || t('tickets.attachmentPreviewAlt', 'Attachment preview')}
                            style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
                        />
                    ) : null}
                    {previewAttachment && previewType === 'video' ? (
                        <Box sx={{ minHeight: 360, height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                            <video
                                src={previewUrl}
                                controls
                                style={{ maxWidth: '100%', maxHeight: '100%', backgroundColor: '#000' }}
                            >
                                <track kind="captions" />
                            </video>
                        </Box>
                    ) : null}
                    {previewAttachment && !previewType ? (
                        <Box sx={{ p: 4 }}>
                            <Alert severity="info">{t('tickets.previewUnavailable', 'Preview is available for PDF, image, and video attachments.')}</Alert>
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    {previewAttachment?.downloadUrl ? (
                        <Button href={previewAttachment.downloadUrl} target="_blank" rel="noopener noreferrer">
                            {t('tickets.download', 'Download')}
                        </Button>
                    ) : null}
                    <Button onClick={() => setPreviewAttachment(null)}>{t('tickets.close', 'Close')}</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default SupportTicketDetails;
